const OTHER_KEY = "__other__";

export function groupDoctorsBySpecialization(doctors, specializations = []) {
  const specOrder = specializations
    .filter((s) => s.is_active !== false)
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

  const bySpec = new Map();
  for (const sp of specOrder) {
    bySpec.set(sp.id, { key: sp.id, title: sp.name, items: [] });
  }
  const other = { key: OTHER_KEY, title: "Без специализации", items: [] };

  for (const doc of doctors) {
    const sid = doc.specialization_id || null;
    if (!sid) {
      other.items.push(doc);
      continue;
    }
    if (!bySpec.has(sid)) {
      bySpec.set(sid, {
        key: sid,
        title: doc.specialization || "Прочее",
        items: [],
      });
    }
    bySpec.get(sid).items.push(doc);
  }

  const groups = [];
  for (const sp of specOrder) {
    const g = bySpec.get(sp.id);
    if (g?.items.length) groups.push(g);
  }
  for (const [id, g] of bySpec) {
    if (!specOrder.some((s) => s.id === id) && g.items.length) {
      groups.push(g);
    }
  }
  if (other.items.length) groups.push(other);
  return groups;
}

export function filterDoctorsBySpec(doctors, filterSpecId) {
  if (!filterSpecId || filterSpecId === "all") return doctors;
  if (filterSpecId === "other" || filterSpecId === "none") {
    return doctors.filter((d) => !d.specialization_id);
  }
  return doctors.filter((d) => d.specialization_id === filterSpecId);
}

/** Тренер явно привязан к залу (из doctor_branches). */
export function doctorAtBranch(doctor, branchId) {
  return (
    Boolean(branchId) &&
    Array.isArray(doctor?.branch_ids) &&
    doctor.branch_ids.includes(branchId)
  );
}

/** Тренеры для записи: зал + услуга + выбранное направление. */
export function filterDoctorsForBooking(
  doctors,
  { serviceId, branchId, specializationId } = {},
) {
  if (!serviceId || !branchId) return [];
  let list = doctors.filter(
    (d) =>
      (d.service_ids || []).includes(serviceId) && doctorAtBranch(d, branchId),
  );
  if (specializationId) {
    list = filterDoctorsBySpec(list, specializationId);
  }
  return list.sort((a, b) => a.full_name.localeCompare(b.full_name, "ru"));
}

/** Записи по специализациям тренера → дни */
/** Записи: специализация → тренер → список занятий */
export function groupAppointmentsBySpecAndDoctor(
  appointments,
  doctors,
  specializations = [],
) {
  const docById = Object.fromEntries(doctors.map((d) => [d.id, d]));
  const specBuckets = new Map();

  for (const a of appointments) {
    const docId = a.doctor?.id || a.doctor_id;
    const doc = docById[docId];
    const sid = doc?.specialization_id || OTHER_KEY;
    if (!specBuckets.has(sid)) {
      specBuckets.set(sid, {
        key: sid,
        title: doc?.specialization || "Без специализации",
        doctors: new Map(),
      });
    }
    const bucket = specBuckets.get(sid);
    if (!bucket.doctors.has(docId)) {
      bucket.doctors.set(docId, {
        id: docId,
        name: doc?.full_name || "Тренер",
        colorId: docId,
        items: [],
      });
    }
    bucket.doctors.get(docId).items.push(a);
  }

  const specOrder = groupDoctorsBySpecialization(doctors, specializations);
  const orderedKeys = specOrder.map((g) => g.key);
  for (const key of specBuckets.keys()) {
    if (!orderedKeys.includes(key)) orderedKeys.push(key);
  }

  return orderedKeys
    .filter((key) => specBuckets.has(key))
    .map((key) => {
      const bucket = specBuckets.get(key);
      const doctorGroups = [...bucket.doctors.values()]
        .map((dg) => ({
          ...dg,
          items: dg.items.sort(
            (x, y) => new Date(x.start_datetime) - new Date(y.start_datetime),
          ),
        }))
        .sort((a, b) => a.name.localeCompare(b.name, "ru"));
      return {
        key: bucket.key,
        title: bucket.title,
        doctors: doctorGroups,
        total: doctorGroups.reduce((n, d) => n + d.items.length, 0),
      };
    });
}

export function groupAppointmentsBySpecialization(
  appointments,
  doctors,
  specializations = [],
) {
  const docById = Object.fromEntries(doctors.map((d) => [d.id, d]));
  const buckets = new Map();

  for (const a of appointments) {
    const docId = a.doctor?.id || a.doctor_id;
    const doc = docById[docId];
    const sid = doc?.specialization_id || OTHER_KEY;
    const title = doc?.specialization || "Без специализации";
    if (!buckets.has(sid)) {
      buckets.set(sid, { key: sid, title, byDate: new Map() });
    }
    const day = new Date(a.start_datetime).toISOString().slice(0, 10);
    const bucket = buckets.get(sid);
    if (!bucket.byDate.has(day)) bucket.byDate.set(day, []);
    bucket.byDate.get(day).push(a);
  }

  const specOrder = groupDoctorsBySpecialization(doctors, specializations);
  const orderedKeys = specOrder.map((g) => g.key);
  for (const key of buckets.keys()) {
    if (!orderedKeys.includes(key)) orderedKeys.push(key);
  }

  return orderedKeys
    .filter((key) => buckets.has(key))
    .map((key) => {
      const bucket = buckets.get(key);
      const days = [...bucket.byDate.entries()]
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([date, items]) => ({
          date,
          items: items.sort(
            (x, y) => new Date(x.start_datetime) - new Date(y.start_datetime),
          ),
        }));
      return { key: bucket.key, title: bucket.title, days };
    });
}

export function countDoctorsBySpec(doctors, specializations = []) {
  const counts = { all: doctors.length, other: 0 };
  for (const sp of specializations) {
    if (sp.is_active === false) continue;
    counts[sp.id] = 0;
  }
  for (const d of doctors) {
    if (!d.specialization_id) {
      counts.other += 1;
    } else if (counts[d.specialization_id] != null) {
      counts[d.specialization_id] += 1;
    }
  }
  return counts;
}

export function trainerGroupIds(doctor) {
  return (doctor?.groups || [])
    .map((g) => (typeof g === "string" ? g : g.id))
    .filter(Boolean);
}

export function filterDoctorsByGroup(doctors, filterGroupId) {
  if (!filterGroupId || filterGroupId === "all") return doctors;
  if (filterGroupId === "none") {
    return doctors.filter((d) => trainerGroupIds(d).length === 0);
  }
  return doctors.filter((d) => trainerGroupIds(d).includes(filterGroupId));
}

export function countDoctorsByGroup(doctors, groups = []) {
  const counts = { all: doctors.length, none: 0 };
  for (const g of groups) {
    if (g.is_active === false) continue;
    counts[g.id] = 0;
  }
  for (const d of doctors) {
    const ids = trainerGroupIds(d);
    if (!ids.length) {
      counts.none += 1;
      continue;
    }
    for (const id of ids) {
      if (counts[id] != null) counts[id] += 1;
    }
  }
  return counts;
}

export function groupDoctorsByTrainingGroup(doctors, groups = []) {
  const groupOrder = groups
    .filter((g) => g.is_active !== false)
    .sort(
      (a, b) =>
        (a.sort_order ?? 0) - (b.sort_order ?? 0) ||
        String(a.name || "").localeCompare(String(b.name || ""), "ru"),
    );

  const byGroup = new Map();
  for (const g of groupOrder) {
    byGroup.set(g.id, { key: g.id, title: g.name, items: [] });
  }
  const unassigned = { key: "none", title: "Без группы", items: [] };

  for (const doc of doctors) {
    const ids = trainerGroupIds(doc);
    if (!ids.length) {
      unassigned.items.push(doc);
      continue;
    }
    for (const gid of ids) {
      if (!byGroup.has(gid)) {
        const name =
          (doc.groups || []).find((g) => g.id === gid)?.name || "Группа";
        byGroup.set(gid, { key: gid, title: name, items: [] });
      }
      byGroup.get(gid).items.push(doc);
    }
  }

  const sortByName = (a, b) =>
    String(a.full_name || "").localeCompare(String(b.full_name || ""), "ru");

  const sections = [];
  for (const g of groupOrder) {
    const section = byGroup.get(g.id);
    if (section?.items.length) {
      section.items.sort(sortByName);
      sections.push(section);
    }
  }
  for (const [id, section] of byGroup) {
    if (!groupOrder.some((g) => g.id === id) && section.items.length) {
      section.items.sort(sortByName);
      sections.push(section);
    }
  }
  if (unassigned.items.length) {
    unassigned.items.sort(sortByName);
    sections.push(unassigned);
  }
  return sections;
}
